import { create } from 'zustand';
import { ConnectUserDto } from '@/components/PkModal/types';

// 多人pk再来一局邀请弹窗数据
export interface MutilPkAgainInviteData {
  inviterName: string;
  pkId: number;
}

// 多人pk连线被邀请弹窗数据
export interface ConnectInviteData {
  inviterName: string; // 邀请人昵称
  fromType: number; // 邀请来源（1互相关注 2PK搜索 3为你推荐 4PK过的 5再来一局）
  connectId: number; // 连线id
  teamSlots: ConnectUserDto[]; // 当前队伍成员列表
}

type MutilPkStore = {
  inviteVisible: boolean;
  inviteData: ConnectInviteData | null;
  showTeamPkModal: boolean; // 是否显示多人PK模式选择弹窗 1-连线中
  teamPkMode: 'multi' | 'brawl';
  showPkGroupModal: boolean; // 是否显示阵容选择弹窗 2-阵容选择
  connectStatus: number; // 连线状态 0-未开始 10-连线中 20-阵容选择 30-pk中
  pkId: number; // 当前PK ID
  profileCardVisible: boolean; // 主播资料卡是否显示
  profileCardAnchorId: number; // 当前展示资料卡的主播ID
  againInviteVisible: boolean; // 多人pk再来一局邀请弹窗是否显示
  againInviteData: MutilPkAgainInviteData | null;
  /** 惩罚倒计时截止时间（绝对 ms 时间戳），0 = 无惩罚阶段。由 MultiPkInfo 写入，供 MultiPkGlobal 读取 */
  punishCountdownDeadline: number;

  actions: {
    showInviteModal: (data: ConnectInviteData) => void;
    hideInviteModal: () => void;
    setShowTeamPkModal: (visible: boolean) => void;
    setTeamPkMode: (mode: 'multi' | 'brawl') => void;
    setShowPkGroupModal: (visible: boolean) => void;
    setConnectStatus: (status: number) => void;
    showProfileCard: (anchorId: number) => void;
    hideProfileCard: () => void;
    setPkId: (pkId: number) => void;
    showAgainInviteModal: (data: MutilPkAgainInviteData) => void;
    hideAgainInviteModal: () => void;
    setPunishCountdownDeadline: (deadline: number) => void;
    resetMutilPkState: () => void;
  };
};

const useMutilPkStore = create<MutilPkStore>()(set => ({
  inviteVisible: false,
  inviteData: null,
  showTeamPkModal: false,
  teamPkMode: 'multi',
  showPkGroupModal: false,
  connectStatus: 0,
  profileCardVisible: false,
  profileCardAnchorId: 0,
  pkId: 0,
  againInviteVisible: false,
  againInviteData: null,
  punishCountdownDeadline: 0,

  actions: {
    showInviteModal: (data: ConnectInviteData) => set({ inviteVisible: true, inviteData: data }),
    hideInviteModal: () => set({ inviteVisible: false, inviteData: null }),
    setShowTeamPkModal: (visible: boolean) => set({ showTeamPkModal: visible }),
    setTeamPkMode: (mode: 'multi' | 'brawl') => set({ teamPkMode: mode }),
    setShowPkGroupModal: (visible: boolean) => set({ showPkGroupModal: visible }),
    setConnectStatus: (status: number) => set({ connectStatus: status }),
    showProfileCard: (anchorId: number) => set({ profileCardVisible: true, profileCardAnchorId: anchorId }),
    hideProfileCard: () => set({ profileCardVisible: false, profileCardAnchorId: 0 }),
    setPkId: (pkId: number) => set({ pkId }),
    showAgainInviteModal: (data: MutilPkAgainInviteData) => set({ againInviteVisible: true, againInviteData: data }),
    hideAgainInviteModal: () => set({ againInviteVisible: false, againInviteData: null }),
    setPunishCountdownDeadline: (deadline: number) => set({ punishCountdownDeadline: deadline }),
    resetMutilPkState: () =>
      set({
        inviteVisible: false,
        inviteData: null,
        showTeamPkModal: false,
        teamPkMode: 'multi',
        showPkGroupModal: false,
        connectStatus: 0,
        profileCardVisible: false,
        profileCardAnchorId: 0,
        pkId: 0,
        againInviteVisible: false,
        againInviteData: null,
        punishCountdownDeadline: 0
      })
  }
}));

export const useMutilPkActions = () => useMutilPkStore(s => s.actions);
export const useMutilPkInvite = () => useMutilPkStore(s => ({ visible: s.inviteVisible, data: s.inviteData }));
export const useTeamPkModalState = () =>
  useMutilPkStore(s => ({ showTeamPkModal: s.showTeamPkModal, teamPkMode: s.teamPkMode }));
export const usePkGroupModalState = () =>
  useMutilPkStore(s => ({ showPkGroupModal: s.showPkGroupModal, connectStatus: s.connectStatus }));

export default useMutilPkStore;
